/**
 * Modal for changing helper identity (first_name, surname, email).
 * Used when identity fields are read-only in the main edit dialog.
 * Admin and Fachbereich Administrator only.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../constants';
import './HelperIdentityChangeModal.css';

export interface IdentityChangeResult {
  first_name: string;
  surname: string;
  email: string;
}

interface HelperIdentityChangeModalProps {
  helper: { id: number; first_name: string; surname: string; email: string | null };
  token: string;
  year: number;
  onSuccess: (newIdentity: IdentityChangeResult) => void;
  onCancel: () => void;
}

const HelperIdentityChangeModal: React.FC<HelperIdentityChangeModalProps> = ({
  helper,
  token,
  year,
  onSuccess,
  onCancel
}) => {
  const [firstName, setFirstName] = useState(helper.first_name || '');
  const [surname, setSurname] = useState(helper.surname || '');
  const [email, setEmail] = useState(helper.email || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [waitingForSync, setWaitingForSync] = useState(false);

  const pollSyncStatus = async (): Promise<boolean> => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/google-sheets/sync-status`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { year }
      });
      return !(res.data?.locked);
    } catch {
      return true;
    }
  };

  const waitForSyncUnlock = async (timeoutMs = 120000): Promise<boolean> => {
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      const unlocked = await pollSyncStatus();
      if (unlocked) return true;
      await new Promise(r => setTimeout(r, 2000));
    }
    return false;
  };

  const handleSave = async () => {
    const fn = firstName.trim();
    const sn = surname.trim();
    const em = email.trim();
    if (!fn || !sn || !em) {
      setError('Vorname, Nachname und E-Mail sind erforderlich.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) {
      setError('Bitte gib eine gültige E-Mail-Adresse ein.');
      return;
    }
    setError('');
    setSaving(true);
    setWaitingForSync(true);
    try {
      const unlocked = await waitForSyncUnlock();
      setWaitingForSync(false);
      if (!unlocked) {
        setError('Synchronisation läuft noch. Bitte in wenigen Sekunden erneut versuchen.');
        setSaving(false);
        return;
      }
      const res = await axios.put(
        `${API_BASE_URL}/api/helper/${helper.id}/identity`,
        { first_name: fn, surname: sn, email: em },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.success) {
        onSuccess({ first_name: fn, surname: sn, email: em });
      } else {
        setError(res.data?.message || 'Fehler beim Speichern');
      }
    } catch (err: unknown) {
      setWaitingForSync(false);
      const msg = (err as { response?: { data?: { message?: string }; status?: number } })?.response?.data?.message
        || (err as Error)?.message
        || 'Fehler beim Speichern';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="helper-identity-modal-overlay">
      <div className="helper-identity-modal">
        <h3>Identitätsdaten ändern</h3>
        <p className="helper-identity-modal-hint">
          Änderungen werden beim Speichern sofort übernommen. Der Dialog „Helfer bearbeiten“ aktualisiert sich automatisch – kein zusätzlicher Speichervorgang nötig. Wenn der Helfer in der Google-Tabelle existiert, wird die Tabelle ebenfalls aktualisiert.
        </p>
        <div className="helper-identity-modal-form">
          <div className="form-group">
            <label htmlFor="identity-firstName">Vorname *</label>
            <input
              id="identity-firstName"
              type="text"
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
              disabled={saving}
              maxLength={100}
            />
          </div>
          <div className="form-group">
            <label htmlFor="identity-surname">Nachname *</label>
            <input
              id="identity-surname"
              type="text"
              value={surname}
              onChange={e => setSurname(e.target.value)}
              disabled={saving}
              maxLength={100}
            />
          </div>
          <div className="form-group">
            <label htmlFor="identity-email">E-Mail *</label>
            <input
              id="identity-email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              disabled={saving}
            />
          </div>
        </div>
        {waitingForSync && (
          <p className="helper-identity-modal-waiting">Synchronisation läuft... Bitte warten.</p>
        )}
        {error && <p className="helper-identity-modal-error">{error}</p>}
        <div className="helper-identity-modal-actions">
          <button type="button" onClick={onCancel} disabled={saving}>
            Abbrechen
          </button>
          <button type="button" onClick={handleSave} disabled={saving}>
            {saving ? 'Speichern...' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HelperIdentityChangeModal;
